// Pure rules behind the cookie banner and analytics: no DOM, no network.
//
// This file stays free of browser globals and is an ES module file (.mjs) so
// `node --test frontend/scripts/privacy-guards.test.js` can load it in CI
// exactly as it is, with nothing installed. The halves that touch the page
// live in consent.js and analytics.js.

export const CONSENT_COOKIE = 'nsofar_consent';
export const GRANTED = 'granted';
export const DENIED = 'denied';

function cookieNames(cookieString) {
  return (cookieString || '')
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter(Boolean);
}

// The stored choice, or null when there is none. An unrecognised value counts
// as no answer, so the visitor is asked again instead of being guessed at.
export function readConsent(cookieString) {
  const match = (cookieString || '').match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`));
  const value = match ? match[1] : null;
  return value === GRANTED || value === DENIED ? value : null;
}

// What Google Analytics leaves behind: _ga, _ga_<stream id>, and the older
// _gid / _gat / _gac_ / _gcl_ family. Not g_state — that one is Google
// sign-in remembering a dismissed prompt, which is functional.
export function analyticsCookieNames(cookieString) {
  return cookieNames(cookieString).filter((name) => /^_(ga|gid|gat|gac|gcl)(_|$)/.test(name));
}

// A shared Health Passport link is a capability URL: the token in its path is
// the only thing protecting the record behind it. Analytics never runs on
// these pages, and the banner is not shown on them.
export function isPrivatePath(pathname) {
  return /^\/passport(\/|$)/.test(pathname || '');
}

// Path segments that name a record rather than a page. Every route in App.js
// with a :parameter needs a line here or in PUBLIC_ROUTE_PARAMS; the CI test
// fails otherwise, so a new route cannot start reporting record ids unnoticed.
const RECORD_PATHS = [
  [/^\/passport\/[^/]+/, '/passport/:token'],
  [/^\/nutritionist\/profile\/[^/]+/, '/nutritionist/profile/:id'],
];
export const PUBLIC_ROUTE_PARAMS = ['/blog/:slug'];

// Campaign labels are the only query parameters analytics has a use for.
// Everything else is dropped: activation codes, chat prompts, payment and
// subscription ids, referral codes.
const CAMPAIGN_PARAM = /^(utm_[a-z_]+|gclid|gbraid|wbraid|dclid)$/;

// A page address as it may be reported to an analytics provider: no fragment,
// no record ids, no query string beyond campaign labels. '' if unparseable.
export function safePageUrl(href) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return '';
  }
  let path = url.pathname;
  RECORD_PATHS.forEach(([pattern, replacement]) => { path = path.replace(pattern, replacement); });
  const kept = [...url.searchParams].filter(([key]) => CAMPAIGN_PARAM.test(key));
  const query = new URLSearchParams(kept).toString();
  return `${url.origin}${path}${query ? `?${query}` : ''}`;
}

// gtag.js watches the page by itself and reports what it sees — a navigation,
// a click on a link, a form being used — through the same queue the page uses
// to talk to it (window.dataLayer). Each of those messages carries an address
// exactly as gtag.js found it: the new and old page address, the link target,
// the form's destination. This recognises them so the queue can refuse them.
// Messages without an address (page lifecycle, scroll depth) pass.
export function carriesRawAddress(message) {
  if (!message || typeof message.event !== 'string' || !message.event.startsWith('gtm.')) return false;
  return Object.keys(message).some((key) => /^gtm\..*Url$/.test(key) && Boolean(message[key]));
}
